import postcss from 'postcss'
import order from './sortOrder.json'
import { emptyRuleRemover, redundantMediaQueriesSquasher, variablize } from './plugins'

type Options = {
	variablize?: boolean
}

const MAXIMUM_FILE_SIZE_IN_BYTES = 2 * 1000 * 1000 // 2Mb

// See https://gist.github.com/mathiasbynens/1010324 for inspiration
const getStringSizeInBytes = (s: string) => encodeURIComponent(s).split(/%..|./).length - 1

export const optimizeCss = async (cssString: string, options: Options = { variablize: false }) => {
	if (getStringSizeInBytes(cssString) > MAXIMUM_FILE_SIZE_IN_BYTES) {
		return cssString
	}

	const plugins = [
		emptyRuleRemover,
		redundantMediaQueriesSquasher,
		/**
		 * This plugin causes issues in Jest due to jsdom creating a weird environment
		 * that is part-node and part-browser, so we just "disable" it in any non-node environment.
		 */
		typeof document === 'undefined' &&
			require('css-declaration-sorter')({
				/**
				 * We're basically running the exact same ordering logic as the default,
				 * while making sure we're also bundling the order JSON.
				 * @see https://github.com/wix-private/thunderbolt/pull/17145
				 */
				order: () => (a: string, b: string) => order.indexOf(a) - order.indexOf(b),
			}),
		require('postcss-discard-empty'),
		require('autoprefixer')({
			add: false,
			// Based on https://github.com/wix-private/editor-server/blob/master/wix-html-server/wix-html-editor/wix-html-editor-core/src/main/java/com/wixpress/html/editor/service/support/UnsupportedBrowsersResolver.scala#L30
			overrideBrowserslist: ['Chrome >= 76', 'Edge >= 18', 'Firefox >= 72', 'Safari >= 11'],
		}),
		require('postcss-discard-unused')({ fontFace: false }),
		require('postcss-merge-idents'),
		require('postcss-reduce-idents'),
		options.variablize && variablize,
	].filter(Boolean)

	const { css: processedCss } = await postcss(plugins).process(cssString, { from: undefined })

	return processedCss
}
